/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  CityMarkerContainer
 */

import { selectAnyFilterSet, selectFilterCount, selectToggleValue } from '@zcode-ui/filters'
import { boxToQueryBounds, isWebGLError, trackGAEvent } from '@zcode/nr-utils'
import { hocCompose, withErrorBoundary } from '@zcode/react-utils'
import type { BoxTuple, State } from '@zcode/state-type'
import { connect } from 'react-redux'
import { ZOOM_CITY } from '../../../constants'
import { ANALYTIC_CHANNEL, CITY_MARKER, EVENT_OPENED } from '../../../constants/marker'
import { logger } from '../../../logger'
import { selectMapDimensions } from '../../../module/map/selectors'
import { setClusterSearchBox } from '../../../module/map/thunks'
import { trackPinClickEvent } from '../../../module/traktor/thunks/trackPinClickEvent'
import type { Dispatch } from '../../../module/types'
import { getFittedMainMapZoom } from '../../../utils/getFittedMainMapZoom'
import { CityMarker } from './CityMarker'

const errorCallback = (error: unknown, info: string | null | undefined, forwardProps: unknown) => {
  if (!isWebGLError(error)) {
    logger.error('ErrorBoundary: caught by CityMarkerContainer', info, forwardProps, error)
  }
}

const mapStateToProps = (state: State, ownProps: { listingCount: number | string }) => {
  const { listingCount } = ownProps
  const filterSet = selectAnyFilterSet(state)
  const setFilterCount = selectFilterCount(state)

  return {
    subText: filterSet && setFilterCount > 0 ? '' : listingCount && `${listingCount} listings`,
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onClick: ({ lat, lng, box }: { lat: number; lng: number; box: BoxTuple }) => {
    trackGAEvent(ANALYTIC_CHANNEL, EVENT_OPENED)

    const queryBounds = boxToQueryBounds(box)
    dispatch((_, getState) => {
      const state = getState()
      const toggleValue = selectToggleValue(state)
      const isFullWidthMap = toggleValue === 'full-map'
      const dimensions = selectMapDimensions(state)
      const zoom = getFittedMainMapZoom({
        box: queryBounds,
        dimensions,
        minZoom: ZOOM_CITY,
        isFullWidthMap,
      })

      dispatch(setClusterSearchBox({ lat, lng, zoom }))
      dispatch(trackPinClickEvent({ lat, lng, markerType: CITY_MARKER }))
    })
  },
})

export const CityMarkerContainer = hocCompose(
  connect(mapStateToProps, mapDispatchToProps),
  withErrorBoundary({ displayName: 'CityMarkerContainer', errorCallback }),
  CityMarker
)
